import { useEffect } from 'react';
import { useThemeOptional } from '../../contexts/ThemeContext';
import { safeLocalStorage } from '../../utils/safeStorage';
import { THEME_STORAGE_KEY, DARK_MODE_ENABLED } from './resolveTheme';
import { safeMatchMedia, subscribeMediaQuery } from '../safeMatchMedia';

/**
 * #3756 — scoped to the guest discovery pages. The marketing site follows
 * prefers-color-scheme; the app pins a visitor with NO stored choice to light
 * (resolveTheme.js interim ship-gate). While a discovery page is mounted and
 * the visitor has never chosen a theme, follow the OS instead so the site ->
 * /discover hop doesn't flip palette. Nothing is persisted, any stored choice
 * (light/dark/system) is left untouched, and on unmount an unset visitor goes
 * back to the app default (light), so the rest of the app is unchanged.
 *
 * #4194: PARKED while DARK_MODE_ENABLED (resolveTheme.js) is false — the hook
 * is a no-op, so a dark-OS visitor sees light on these pages. The behaviour
 * above returns when the dark-mode overhaul flips the switch.
 */
export function readHasStoredTheme() {
  const v = safeLocalStorage.getItem(THEME_STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system';
}

export function useSystemThemeWhenUnset() {
  const applyTransientTheme = useThemeOptional()?.applyTransientTheme;
  useEffect(() => {
    // #4194: dark mode is off — stay light on a dark OS, attach no listener.
    if (!DARK_MODE_ENABLED || !applyTransientTheme || readHasStoredTheme()) {
      return undefined;
    }
    // #4215: null (absent/throwing matchMedia) -> no-op effect, as before for "absent".
    const mql = safeMatchMedia('(prefers-color-scheme: dark)');
    if (!mql) return undefined;
    applyTransientTheme(mql.matches ? 'dark' : 'light');
    const onChange = (e) => applyTransientTheme(e.matches ? 'dark' : 'light');
    const unsubscribe = subscribeMediaQuery(mql, onChange);
    return () => {
      unsubscribe();
      if (!readHasStoredTheme()) applyTransientTheme('light');
    };
  }, [applyTransientTheme]);
}
